import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CommanServices } from '@/services/commanServices';
import socketService from '@/services/socketService';
import { Marker, InfoWindow, Polygon, Circle, Polyline, DrawingManager } from '@react-google-maps/api';
import { MapPin, Flame, Activity, ShieldCheck, Crosshair, Pencil, Flame as FlameIcon,
  Trash2, Plus, Route, Layers3, X, Target, Square, CheckCircle2, Circle as CircleIcon, Spline, ChevronDown } from 'lucide-react';
import { MAP_STYLE_PRESETS } from './GoogleMapsConfig';
export { MAP_LIBRARIES, GOOGLE_MAPS_LOADER_OPTIONS, MAP_STYLE_PRESETS, buildMapStyleOptions } from './GoogleMapsConfig';

// --- Real-world measurements for drawn shapes (haversine — no extra libs) ---
const _distKm = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};
const _fmtKm = (km) => (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(km < 10 ? 2 : 1)} km`);
export const pathLengthKm = (path = []) => path.slice(1).reduce((sum, p, i) => sum + _distKm(path[i], p), 0);
// Spherical polygon area (km²) — good accuracy at zone scale.
export const polygonAreaKm2 = (paths = []) => {
  if (paths.length < 3) return 0;
  const R = 6371;
  let total = 0;
  for (let i = 0; i < paths.length; i++) {
    const p1 = paths[i], p2 = paths[(i + 1) % paths.length];
    total += ((p2.lng - p1.lng) * Math.PI / 180) *
      (2 + Math.sin(p1.lat * Math.PI / 180) + Math.sin(p2.lat * Math.PI / 180));
  }
  return Math.abs((total * R * R) / 2);
};
export const shapeMeasure = (z) => {
  if (z.kind === 'circle') return `r = ${_fmtKm((z.radius || 0) / 1000)}`;
  if (z.kind === 'polyline') return _fmtKm(pathLengthKm(z.path || []));
  if (z.kind === 'polygon') return `${polygonAreaKm2(z.paths || []).toFixed(2)} km² · ${_fmtKm(pathLengthKm([...(z.paths || []), (z.paths || [])[0]].filter(Boolean)))} around`;
  return '';
};
const shapeLabelPos = (z) => {
  if (z.kind === 'circle') return z.center;
  const pts = z.kind === 'polyline' ? (z.path || []) : (z.paths || []);
  if (!pts.length) return null;
  if (z.kind === 'polyline') return pts[Math.floor(pts.length / 2)];
  return { lat: pts.reduce((s, p) => s + p.lat, 0) / pts.length, lng: pts.reduce((s, p) => s + p.lng, 0) / pts.length };
};

// Compact dropdown to switch the map style on any map.
export const MapStyleSelector = ({ value, onChange, className = '', testid = 'map-style-selector' }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className={`text-xs font-bold border border-gray-200 rounded-lg bg-white/95 text-gray-700 px-2 py-1.5 shadow-sm ${className}`}>
    {Object.entries(MAP_STYLE_PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
  </select>
);


export const IMPORTANCE = {
  readiness: { label: 'Readiness', color: '#3b82f6' },
  response: { label: 'Response', color: '#f59e0b' },
  activation: { label: 'Activation', color: '#ef4444' },
  resolved: { label: 'Resolved', color: '#22c55e' },
  black: { label: 'Black', color: '#111827' },
};
export const FULL_MAP_IMPORTANCE_COLORS = {
  readiness: '#2563eb',
  response: '#f59e0b',
  activation: '#ef4444',
  resolved: '#16a34a',
  black: '#111827',
};
export const CATEGORIES = ['Rescue', 'Medical', 'Fire', 'Security', 'Evacuation', 'Logistics'];
const IMP_WEIGHT = { readiness: 1, response: 2, activation: 3, resolved: 0.5, black: 1 };

// Map point-of-interest types (assembly/muster points, info markers, etc.)
export const POINT_TYPES = {
  assembly: { label: 'Assembly Point', color: '#16a34a', glyph: 'A' },
  info: { label: 'Info Point', color: '#2563eb', glyph: 'i' },
  command: { label: 'Command Post', color: '#0369a1', glyph: 'C' },
  medical: { label: 'Medical Point', color: '#dc2626', glyph: '+' },
  shelter: { label: 'Shelter', color: '#0284c7', glyph: 'S' },
  muster: { label: 'Muster Station', color: '#b45309', glyph: 'M' },
  hazard: { label: 'Hazard', color: '#ea580c', glyph: '!' },
  evacuation: { label: 'Evacuation', color: '#0d9488', glyph: 'E' },
};

const load = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const save = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ } };

// Append a task to the shared GIS map store (used to link incident action cards to the map)
export const pushGisTask = (task) => {
  try {
    const arr = JSON.parse(localStorage.getItem('gis_tasks') || '[]');
    arr.push({ id: `task_${Date.now()}`, createdAt: Date.now(), category: 'Rescue', importance: 'response', status: 'open', ...task });
    localStorage.setItem('gis_tasks', JSON.stringify(arr));
    return true;
  } catch { return false; }
};

export const useMapGis = () => {
  const [tasks, setTasks] = useState(() => load('gis_tasks', []));
  const [zones, setZones] = useState(() => load('gis_zones', []));
  const [points, setPoints] = useState(() => load('gis_points', []));
  useEffect(() => save('gis_tasks', tasks), [tasks]);
  useEffect(() => save('gis_zones', zones), [zones]);
  useEffect(() => save('gis_points', points), [points]);
  return { tasks, setTasks, zones, setZones, points, setPoints };
};

// Backend-backed GIS map for a specific scenario/drill (its backing incident).
// Loads the saved payload (tasks, zones, mapInfo, settings) and auto-saves on
// change (debounced) while exposing a manual saveNow() + saveStatus indicator.
export const usePersistentGisMap = (incidentId, enabled = true, writable = true) => {
  const [tasks, setTasks] = useState([]);
  const [zones, setZones] = useState([]);
  const [points, setPoints] = useState([]);
  const [mapInfo, setMapInfo] = useState({});
  const [settings, setSettings] = useState({});
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved
  const [loaded, setLoaded] = useState(false);
  const skipNextSave = useRef(true);
  const appliedSignature = useRef('');
  // External writes to the incident's gis_map (e.g. "Activate scenario map")
  // dispatch 'gisMapUpdated' so read-only previews reload without a page refresh.
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    const h = (e) => {
      const target = e?.detail?.incidentId;
      if (!target || target === incidentId) setReloadTick((t) => t + 1);
    };
    window.addEventListener('gisMapUpdated', h);
    return () => window.removeEventListener('gisMapUpdated', h);
  }, [incidentId]);

  const applyRemoteMap = useCallback((rawMap, external = false) => {
    const gm = rawMap && typeof rawMap === 'object' ? rawMap : {};
    const normalized = {
      tasks: Array.isArray(gm.tasks) ? gm.tasks : [],
      zones: Array.isArray(gm.zones) ? gm.zones : [],
      points: Array.isArray(gm.points) ? gm.points : [],
      mapInfo: gm.mapInfo && typeof gm.mapInfo === 'object' ? gm.mapInfo : {},
      settings: gm.settings && typeof gm.settings === 'object' ? gm.settings : {},
    };
    const signature = JSON.stringify(normalized);
    if (external && signature === appliedSignature.current) return false;
    if (external) skipNextSave.current = true;
    appliedSignature.current = signature;
    setTasks(normalized.tasks);
    setZones(normalized.zones);
    setPoints(normalized.points);
    setMapInfo(normalized.mapInfo);
    setSettings(normalized.settings);
    const hasData = normalized.tasks.length || normalized.zones.length || normalized.points.length
      || Object.keys(normalized.mapInfo).length || Object.keys(normalized.settings).length;
    setSaveStatus(hasData ? 'saved' : 'idle');
    return true;
  }, []);

  useEffect(() => {
    if (!enabled || !incidentId) return undefined;
    return socketService.on('receive_incident_sync', (event) => {
      const targetId = String(event?.incident_id || event?.incidentId || '');
      const changeType = String(event?.change_type || '').toLowerCase();
      if (targetId !== String(incidentId) || !['gis_map_updated', 'scenario_map_applied'].includes(changeType)) return;
      const remoteMap = event?.payload?.gis_map;
      if (remoteMap) applyRemoteMap(remoteMap, true);
      else setReloadTick((tick) => tick + 1);
    });
  }, [applyRemoteMap, enabled, incidentId]);

  useEffect(() => {
    let cancelled = false;
    if (!enabled || !incidentId) { setLoaded(false); return; }
    setLoaded(false);
    skipNextSave.current = true;
    (async () => {
      try {
        const res = await CommanServices.getIncidentGisMap(incidentId);
        const gm = res?.data?.gis_map || {};
        if (cancelled) return;
        applyRemoteMap(gm);
      } catch { /* noop */ }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [incidentId, enabled, reloadTick, applyRemoteMap]);

  // Debounced auto-save once the initial load is done.
  useEffect(() => {
    if (!enabled || !incidentId || !loaded || !writable) return;
    if (skipNextSave.current) { skipNextSave.current = false; return; }
    setSaveStatus('saving');
    const handle = setTimeout(async () => {
      const mapPayload = { tasks, zones, points, mapInfo, settings };
      appliedSignature.current = JSON.stringify(mapPayload);
      try {
        await CommanServices.saveIncidentGisMap(incidentId, mapPayload);
        setSaveStatus('saved');
      } catch { setSaveStatus('idle'); }
    }, 300);
    return () => clearTimeout(handle);
  }, [tasks, zones, points, mapInfo, settings, enabled, incidentId, loaded, writable]);

  const saveNow = useCallback(async () => {
    if (!enabled || !incidentId || !writable) return;
    setSaveStatus('saving');
    const mapPayload = { tasks, zones, points, mapInfo, settings };
    appliedSignature.current = JSON.stringify(mapPayload);
    try {
      await CommanServices.saveIncidentGisMap(incidentId, mapPayload);
      setSaveStatus('saved');
    } catch { setSaveStatus('idle'); }
  }, [enabled, incidentId, tasks, zones, points, mapInfo, settings, writable]);

  return { tasks, setTasks, zones, setZones, points, setPoints, mapInfo, setMapInfo, settings, setSettings, saveStatus, saveNow, loaded };
};

// Small reusable "Save Map" button + status indicator for scenario/drill maps.
export const GisSaveControl = ({ saveStatus, onSave, className = '', statusFirst = false, compactOnSmall = false }) => {
  const saveButton = (
    <button type="button" aria-label="Save map" onClick={onSave} data-testid="gis-save-map-btn"
      className={`flex items-center gap-1.5 py-1.5 rounded-lg text-xs font-bold shadow-sm border bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-500 transition-colors ${compactOnSmall ? 'px-2 sm:px-3' : 'px-3'}`}>
      <CheckCircle2 className="w-3.5 h-3.5" />
      <span className={compactOnSmall ? 'hidden sm:inline' : ''}>Save Map</span>
    </button>
  );
  const saveIndicator = (
    <span data-testid="gis-save-status" className={`text-[11px] font-semibold ${compactOnSmall ? 'hidden sm:inline' : ''} ${saveStatus === 'saved' ? 'text-emerald-600' : saveStatus === 'saving' ? 'text-amber-600' : 'text-gray-400'}`}>
      {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved ✓' : 'Not saved'}
    </span>
  );

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="gis-save-control">
      {statusFirst && saveIndicator}
      {saveButton}
      {!statusFirst && saveIndicator}
    </div>
  );
};

// ── Left-panel tools ──────────────────────────────────────────────────────────
export const MapGisPanel = ({
  tasks, addMode, setAddMode, drawShape, setDrawShape, heatmapOn, setHeatmapOn,
  sequenceOn, setSequenceOn, activeImportance, setActiveImportance, newCategory, setNewCategory,
  onClear, pointMode, setPointMode, pointType, setPointType, points = [], onGeoFence = null,
  variant = 'default', floatingPlacement = 'overlay', floatingDefaultCollapsed = false,
}) => {
  const [floatingCollapsed, setFloatingCollapsed] = useState(floatingDefaultCollapsed);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const counts = useMemo(() => {
    const c = { readiness: 0, response: 0, activation: 0, resolved: 0 };
    tasks.forEach(t => { c[t.importance] = (c[t.importance] || 0) + 1; });
    return c;
  }, [tasks]);

  const ToolBtn = ({ active, onClick, icon, label, testid }) => {
    const destructive = testid === 'gis-clear';
    return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={active}
      onClick={onClick}
      className={`flex min-h-[44px] items-center gap-2 rounded-xl border px-2.5 py-2 text-left text-[10.5px] font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${active
        ? 'border-slate-900 bg-gradient-to-br from-slate-900 to-sky-800 text-white shadow-[0_8px_18px_-12px_rgba(2,132,199,0.85)]'
        : destructive
          ? 'border-red-200 bg-red-50/70 text-red-700 hover:border-red-300 hover:bg-red-50'
          : 'border-slate-200 bg-white text-slate-700 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-800'}`}
    >
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${active ? 'bg-white/15 text-sky-100' : destructive ? 'bg-red-100 text-red-600' : 'bg-sky-50 text-sky-700'}`}>{icon}</span>
      <span className="leading-tight">{label}</span>
    </button>
    );
  };

  if (variant === 'floating') {
    const floatingSwatches = FULL_MAP_IMPORTANCE_COLORS;
    const shortPointTypeLabels = {
      assembly: 'Assembly',
      info: 'Info',
      command: 'Command',
      medical: 'Medical',
      shelter: 'Shelter',
      muster: 'Muster',
      hazard: 'Hazard',
    };
    const FloatingToolBtn = ({ active, onClick, icon, label, testid }) => (
      <button
        type="button"
        data-testid={testid}
        aria-pressed={active}
        onClick={onClick}
        className={`flex min-h-[42px] w-full items-center gap-2 rounded-xl border px-2 py-1.5 text-left text-[11.5px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${active
          ? 'border-cyan-500/60 bg-cyan-500/15 text-cyan-100'
          : 'border-slate-700 bg-slate-900/75 text-slate-300 hover:border-slate-600 hover:bg-slate-800 hover:text-cyan-200'}`}
      >
        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${active ? 'bg-cyan-500/20 text-cyan-200' : 'bg-slate-800 text-slate-400'}`}>
          {icon}
        </span>
        <span className="min-w-0 leading-tight">{label}</span>
      </button>
    );

    return (
      <div
        className={`${floatingPlacement === 'flow'
          ? 'pointer-events-auto relative max-h-[min(34rem,calc(100vh-10rem))] w-full shrink-0'
          : 'absolute left-[14px] top-[14px] z-20 max-h-[calc(100%_-_150px)] w-[264px] max-w-[calc(100%_-_28px)] sm:max-h-[calc(100%_-_28px)]'} flex flex-col overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-950/90 shadow-[0_22px_54px_-26px_rgba(0,0,0,0.92)] backdrop-blur-xl`}
        data-testid="gis-tools-panel"
        data-variant="floating"
        data-placement={floatingPlacement}
        data-surface="tactical-dark"
      >
        <div className={`flex shrink-0 items-center justify-between bg-slate-950/55 px-3.5 py-3 ${floatingCollapsed ? '' : 'border-b border-slate-800'}`}>
          <div className="flex min-w-0 items-center gap-2 font-semibold text-slate-100">
            <Layers3 className="h-4 w-4 shrink-0 text-cyan-300" />
            <span className="truncate text-sm">GIS Tasks &amp; Tools</span>
          </div>
          <button
            type="button"
            aria-expanded={!floatingCollapsed}
            onClick={() => setFloatingCollapsed((value) => !value)}
            className="ml-2 shrink-0 text-[10px] font-semibold text-slate-500 transition-colors hover:text-cyan-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
          >
            {floatingCollapsed ? '▸ expand' : '▾ collapse'}
          </button>
        </div>

        {!floatingCollapsed && (
          <div
            className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            data-testid="gis-tools-scroll-content"
          >
            <section>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Draw tools</p>
                <button
                  type="button"
                  data-testid="gis-clear"
                  onClick={onClear}
                  className="flex items-center gap-1 text-[10px] font-bold text-red-400 transition-colors hover:text-red-300"
                >
                  <Trash2 className="h-3 w-3" /> Clear
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {onGeoFence ? (
                  <FloatingToolBtn
                    testid="gis-geo-fence"
                    active={false}
                    onClick={onGeoFence}
                    icon={<ShieldCheck className="h-3.5 w-3.5" />}
                    label="Geo-Fence"
                  />
                ) : (
                  <FloatingToolBtn
                    testid="gis-add-task"
                    active={Boolean(addMode)}
                    onClick={() => { setAddMode((value) => !value); setDrawShape('none'); setPointMode?.(false); }}
                    icon={<Crosshair className="h-3.5 w-3.5" />}
                    label="Add Task"
                  />
                )}
                <FloatingToolBtn
                  testid="gis-add-point"
                  active={Boolean(pointMode)}
                  onClick={() => { setPointMode?.((value) => !value); setAddMode(false); setDrawShape('none'); }}
                  icon={<MapPin className="h-3.5 w-3.5" />}
                  label="Add Point"
                />
                <FloatingToolBtn
                  testid="gis-draw-zone"
                  active={drawShape === 'polygon'}
                  onClick={() => { setDrawShape((shape) => shape === 'polygon' ? 'none' : 'polygon'); setAddMode(false); setPointMode?.(false); }}
                  icon={<Square className="h-3.5 w-3.5" />}
                  label="Zone"
                />
                <FloatingToolBtn
                  testid="gis-draw-circle"
                  active={drawShape === 'circle'}
                  onClick={() => { setDrawShape((shape) => shape === 'circle' ? 'none' : 'circle'); setAddMode(false); setPointMode?.(false); }}
                  icon={<CircleIcon className="h-3.5 w-3.5" />}
                  label="Radius"
                />
                <FloatingToolBtn
                  testid="gis-draw-line"
                  active={drawShape === 'polyline'}
                  onClick={() => { setDrawShape((shape) => shape === 'polyline' ? 'none' : 'polyline'); setAddMode(false); setPointMode?.(false); }}
                  icon={<Spline className="h-3.5 w-3.5" />}
                  label="Line"
                />
                <FloatingToolBtn
                  testid="gis-heatmap"
                  active={Boolean(heatmapOn)}
                  onClick={() => setHeatmapOn((value) => !value)}
                  icon={<FlameIcon className="h-3.5 w-3.5" />}
                  label="Heatmap"
                />
                <FloatingToolBtn
                  testid="gis-sequence"
                  active={Boolean(sequenceOn)}
                  onClick={() => setSequenceOn((value) => !value)}
                  icon={<Route className="h-3.5 w-3.5" />}
                  label="Sequence"
                />
              </div>
            </section>

            <section className="flex items-center gap-2 border-t border-slate-800 pt-3">
              <p className="mr-auto text-[10px] font-bold uppercase tracking-wider text-slate-400">Color</p>
              {Object.entries(floatingSwatches).map(([key, color]) => (
                <button
                  type="button"
                  key={key}
                  data-testid={`gis-imp-${key}`}
                  title={IMPORTANCE[key]?.label}
                  aria-label={`${IMPORTANCE[key]?.label} color`}
                  aria-pressed={activeImportance === key}
                  onClick={() => setActiveImportance(key)}
                  className={`h-5 w-5 shrink-0 rounded-full border-2 border-slate-700 transition-transform hover:scale-110 ${activeImportance === key ? 'ring-2 ring-cyan-400 ring-offset-1 ring-offset-slate-950' : 'shadow-sm'}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </section>

            {setPointMode && (
              <section>
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Point type</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(POINT_TYPES).map(([key, point]) => (
                    <button
                      type="button"
                      key={key}
                      data-testid={`gis-point-type-${key}`}
                      onClick={() => { setPointType(key); setPointMode(true); setAddMode(false); setDrawShape('none'); }}
                      className={`flex items-center gap-1 rounded-full border px-1.5 py-1 text-[9.5px] font-bold transition-colors ${pointType === key
                        ? 'border-cyan-500/60 bg-cyan-500/15 text-cyan-100'
                        : 'border-slate-700 bg-slate-900/75 text-slate-400 hover:border-slate-600 hover:text-slate-200'}`}
                    >
                      <span
                        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-black text-white"
                        style={{ backgroundColor: point.color }}
                      >
                        {point.glyph}
                      </span>
                      {shortPointTypeLabels[key]}
                    </button>
                  ))}
                </div>
              </section>
            )}

            <section>
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Task category</p>
              <select
                value={newCategory}
                onChange={(event) => setNewCategory(event.target.value)}
                data-testid="gis-category"
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs text-slate-100 focus:border-cyan-500 focus:outline-none"
              >
                {CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
            </section>

            <section className="border-t border-slate-800 pt-3">
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Task analytics</p>
              <div className="grid grid-cols-2 gap-1.5">
                {Object.entries(IMPORTANCE).filter(([key]) => key !== 'black').map(([key, importance]) => (
                  <div key={key} className="flex min-w-0 items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900/75 px-2 py-1.5 text-[10px]">
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: floatingSwatches[key] || importance.color }} />
                    <span className="min-w-0 flex-1 truncate font-semibold text-slate-400">{importance.label}</span>
                    <span className="font-black text-slate-100">{counts[key] || 0}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="space-y-4 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_12px_32px_-24px_rgba(15,23,42,0.48)]"
      data-testid="gis-tools-panel"
    >
      <button
        type="button"
        onClick={() => setPanelCollapsed((v) => !v)}
        className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
        aria-expanded={!panelCollapsed}
        data-testid="gis-tools-collapse"
      >
        <span className="flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-[13px] bg-gradient-to-br from-slate-900 to-sky-700 text-white shadow-[0_10px_22px_-12px_rgba(2,132,199,0.85)]">
          <Layers3 className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[16.5px] font-bold" style={{ color: '#0f172a' }}>GIS Tasks &amp; Tools</span>
          <span className="block truncate text-[11.5px]" style={{ color: '#64748b' }}>Draw, annotate and assign on the live map</span>
        </span>
        <span className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-500">
          <ChevronDown className={`h-4 w-4 transition-transform ${panelCollapsed ? '' : 'rotate-180'}`} />
        </span>
      </button>
      {!panelCollapsed && (<>

      {/* Color picker — choose the color for tasks & drawings */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Color</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(IMPORTANCE).map(([k, v]) => (
            <button key={k} data-testid={`gis-imp-${k}`} title={v.label} onClick={() => setActiveImportance(k)}
              className="h-8 w-8 rounded-full transition-transform hover:scale-110"
              style={{ backgroundColor: v.color, boxShadow: activeImportance === k ? `0 0 0 3px #fff, 0 0 0 5.5px ${v.color}` : '0 1px 3px rgba(15,23,42,0.2)' }} />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {onGeoFence ? (
          <ToolBtn
            testid="gis-geo-fence"
            active={false}
            onClick={onGeoFence}
            icon={<Target className="w-3.5 h-3.5" />}
            label="Geo-Fence"
          />
        ) : (
          <ToolBtn testid="gis-add-task" active={addMode} onClick={() => { setAddMode(v => !v); setDrawShape('none'); setPointMode && setPointMode(false); }} icon={<Crosshair className="w-3.5 h-3.5" />} label="Add Task" />
        )}
        {setPointMode && (
          <ToolBtn testid="gis-add-point" active={pointMode} onClick={() => { setPointMode(v => !v); setAddMode(false); setDrawShape('none'); }} icon={<MapPin className="w-3.5 h-3.5" />} label="Add Point" />
        )}
        <ToolBtn testid="gis-draw-zone" active={drawShape === 'polygon'} onClick={() => { setDrawShape(s => s === 'polygon' ? 'none' : 'polygon'); setAddMode(false); setPointMode && setPointMode(false); }} icon={<Pencil className="w-3.5 h-3.5" />} label="Zone" />
        <ToolBtn testid="gis-draw-circle" active={drawShape === 'circle'} onClick={() => { setDrawShape(s => s === 'circle' ? 'none' : 'circle'); setAddMode(false); setPointMode && setPointMode(false); }} icon={<Target className="w-3.5 h-3.5" />} label="Radius" />
        <ToolBtn testid="gis-draw-line" active={drawShape === 'polyline'} onClick={() => { setDrawShape(s => s === 'polyline' ? 'none' : 'polyline'); setAddMode(false); setPointMode && setPointMode(false); }} icon={<Spline className="w-3.5 h-3.5" />} label="Line" />
        <ToolBtn testid="gis-heatmap" active={heatmapOn} onClick={() => setHeatmapOn(v => !v)} icon={<FlameIcon className="w-3.5 h-3.5" />} label="Heatmap" />
        <ToolBtn testid="gis-sequence" active={sequenceOn} onClick={() => setSequenceOn(v => !v)} icon={<Route className="w-3.5 h-3.5" />} label="Sequence" />
        <ToolBtn testid="gis-clear" active={false} onClick={onClear} icon={<Trash2 className="w-3.5 h-3.5" />} label="Clear" />
      </div>

      {addMode && <p className="rounded-lg bg-sky-50 px-2.5 py-2 text-[11px] font-semibold text-sky-800 ring-1 ring-sky-100">Click anywhere on the map to drop a task.</p>}
      {pointMode && <p className="text-[11px] text-emerald-600 bg-emerald-50 rounded-md px-2 py-1.5">Click on the map to drop a {POINT_TYPES[pointType]?.label || 'point'}.</p>}
      {drawShape === 'polyline' && <p className="text-[11px] text-amber-600 bg-amber-50 rounded-md px-2 py-1.5">Click points on the map to draw a line; double-click to finish.</p>}
      {drawShape !== 'none' && drawShape !== 'polyline' && <p className="text-[11px] text-amber-600 bg-amber-50 rounded-md px-2 py-1.5">Draw a {drawShape === 'circle' ? 'radius' : 'zone'} on the map.</p>}

      {/* Point type selector (assembly / info / command / etc.) */}
      {setPointMode && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Point type</p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(POINT_TYPES).map(([k, v]) => (
              <button key={k} data-testid={`gis-point-type-${k}`} onClick={() => { setPointType(k); setPointMode(true); setAddMode(false); setDrawShape('none'); }}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border ${pointType === k ? 'ring-2 ring-offset-1' : ''}`}
                style={{ color: v.color, borderColor: v.color, backgroundColor: `${v.color}14`, ['--tw-ring-color']: v.color }}>
                <span className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px]" style={{ backgroundColor: v.color }}>{v.glyph}</span>{v.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Importance moved above the tool buttons */}

      {/* Category */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Task category</p>
        <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)} data-testid="gis-category"
          className="w-full text-xs border border-gray-300 rounded-md text-gray-900 bg-white px-2 py-1.5">
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Analytics by importance */}
      <div className="pt-1 border-t">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Task analytics</p>
        <div className="grid grid-cols-2 gap-1.5">
          {Object.entries(IMPORTANCE).filter(([k]) => k !== 'black').map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-[11px] rounded-md px-2 py-1" style={{ backgroundColor: `${v.color}12` }}>
              <span className="flex items-center gap-1 font-semibold" style={{ color: v.color }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: v.color }} />{v.label}</span>
              <span className="font-bold" style={{ color: v.color }}>{counts[k] || 0}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[11px] text-gray-500 mt-2 px-1">
          <span>Total tasks</span><span className="font-bold text-gray-800">{tasks.length}</span>
        </div>
        <div className="flex justify-between text-[11px] text-gray-500 px-1">
          <span>Map points</span><span className="font-bold text-gray-800">{points.length}</span>
        </div>
      </div>
      </>)}
    </div>
  );
};

// ── Floating operational summary on the map ───────────────────────────────────
export const MapGisKpi = ({ summary = {}, variant = 'default' }) => {
  const statItems = [
    { l: 'Incidents', v: summary.real ?? 0, c: 'text-red-400', dot: 'bg-red-400' },
    { l: 'Drills', v: summary.drills ?? 0, c: 'text-sky-300', dot: 'bg-sky-400' },
    { l: 'Active', v: summary.active ?? 0, c: 'text-emerald-400', dot: 'bg-emerald-400' },
    { l: 'Related', v: summary.relatedIncidents ?? 0, c: 'text-cyan-300', dot: 'bg-cyan-400' },
    { l: 'Teams', v: summary.teams ?? 0, c: 'text-slate-100', dot: 'bg-slate-400' },
    { l: 'Users', v: summary.users ?? 0, c: 'text-slate-100', dot: 'bg-slate-500' },
  ];

  if (variant === 'strip') {
    return (
      <div className="pointer-events-none absolute bottom-4 left-[14px] right-[66px] z-10 flex justify-center sm:left-[292px]" data-testid="gis-kpi-card" data-variant="strip">
        <div className="pointer-events-auto flex w-fit max-w-full overflow-x-auto rounded-xl border border-slate-700/80 bg-slate-950/90 shadow-2xl backdrop-blur" data-surface="tactical-dark">
          <div className="flex min-w-[132px] flex-col justify-center px-4 py-2.5">
            <p className="text-[9px] font-black uppercase tracking-[0.12em] text-slate-300">Operations Summary</p>
            <p className="mt-1 inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]" />
              Live
            </p>
          </div>
          {statItems.map((stat) => (
            <div key={stat.l} className="flex min-w-[86px] flex-col items-center justify-center border-l border-slate-800 px-4 py-2 text-center">
              <p className={`text-lg font-black leading-none tabular-nums ${stat.c}`}>{stat.v}</p>
              <p className="mt-1 flex items-center gap-1 text-[8px] font-bold uppercase tracking-wider text-slate-400">
                <span className={`h-1.5 w-1.5 flex-none rounded-full ${stat.dot}`} />
                {stat.l}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="absolute bottom-4 left-4 z-10 w-64 rounded-2xl border border-slate-700/80 bg-slate-950/90 p-3.5 shadow-2xl backdrop-blur" data-testid="gis-kpi-card" data-surface="tactical-dark">
      <div className="mb-2.5 flex items-center justify-between gap-3">
        <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-300">Operations Summary</p>
        <p className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-emerald-400">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]" />
          Live
        </p>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {statItems.map(s => (
          <div key={s.l} className="rounded-lg border border-slate-800 bg-slate-900/75 px-2 py-1.5">
            <p className={`text-base font-black tabular-nums ${s.c}`}>{s.v}</p>
            <p className="mt-0.5 flex items-center gap-1 text-[8px] font-semibold leading-tight text-slate-400">
              <span className={`h-1.5 w-1.5 flex-none rounded-full ${s.dot}`} />
              {s.l}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── In-map overlay (markers, zones, heatmap, sequence, drawing) ────────────────
export const MapGisOverlay = ({
  tasks, setTasks, zones, setZones, points = [], setPoints, onEditPoint,
  drawShape, setDrawShape, heatmapOn, sequenceOn,
  activeImportance, selectedTaskId, setSelectedTaskId, onEditTask, incidents, incidentId,
  readOnly = false, importanceColors = null,
}) => {
  const g = window.google;
  const [selectedPointId, setSelectedPointId] = useState(null);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const importanceColor = (key) => importanceColors?.[key] || IMPORTANCE[key]?.color || '#0284c7';

  const taskIcon = (imp) => g ? ({
    path: g.maps.SymbolPath.CIRCLE,
    fillColor: importanceColor(imp),
    fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2, scale: 8,
  }) : undefined;

  const pointIcon = (type) => g ? ({
    path: g.maps.SymbolPath.CIRCLE,
    fillColor: POINT_TYPES[type]?.color || '#2563eb',
    fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2, scale: 9,
  }) : undefined;

  const heatPoints = useMemo(() => {
    if (!heatmapOn) return [];
    const pts = [];
    tasks.forEach(t => t.position && pts.push({ lat: t.position.lat, lng: t.position.lng, weight: IMP_WEIGHT[t.importance] || 1 }));
    (incidents || []).forEach(inc => {
      const lat = inc.gps_latitude ?? inc.gps_coordinates?.lat;
      const lng = inc.gps_longitude ?? inc.gps_coordinates?.lng;
      if (lat && lng) pts.push({ lat, lng, weight: 2 });
    });
    return pts;
  }, [heatmapOn, tasks, incidents]);

  const sequencePath = useMemo(() => {
    if (!sequenceOn) return [];
    return [...tasks].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0)).filter(t => t.position).map(t => t.position);
  }, [sequenceOn, tasks]);

  if (!g) return null;

  const toggleSubtask = (taskId, i) => setTasks(ts => ts.map(t => t.id === taskId
    ? { ...t, tasks: (t.tasks || []).map((s, idx) => idx === i ? { ...s, done: !s.done } : s) }
    : t));

  const handleOverlayComplete = (e) => {
    const ov = e.overlay;
    const color = importanceColor(activeImportance);
    if (e.type === 'polygon') {
      const paths = ov.getPath().getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));
      setZones(z => [...z, { id: `z_${Date.now()}`, kind: 'polygon', paths, color, importance: activeImportance, incidentId }]);
    } else if (e.type === 'circle') {
      const c = ov.getCenter();
      setZones(z => [...z, { id: `z_${Date.now()}`, kind: 'circle', center: { lat: c.lat(), lng: c.lng() }, radius: ov.getRadius(), color, importance: activeImportance, incidentId }]);
    } else if (e.type === 'polyline') {
      const path = ov.getPath().getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));
      setZones(z => [...z, { id: `z_${Date.now()}`, kind: 'polyline', path, color, importance: activeImportance, incidentId }]);
    }
    ov.setMap(null);
    setDrawShape('none');
  };

  return (
    <>
      {heatmapOn && heatPoints.map((p, i) => (
        <Circle key={`heat-${i}`} center={{ lat: p.lat, lng: p.lng }}
          radius={260 + p.weight * 150}
          options={{ fillColor: '#ef4444', fillOpacity: Math.min(0.1 + p.weight * 0.05, 0.32), strokeOpacity: 0, clickable: false, zIndex: 5 }} />
      ))}

      {zones.map(z => z.kind === 'polygon' ? (
        <Polygon key={z.id} paths={z.paths} onClick={() => setSelectedZoneId(z.id)} options={{ fillColor: z.color, fillOpacity: 0.18, strokeColor: z.color, strokeWeight: 2, clickable: true }} />
      ) : z.kind === 'polyline' ? (
        <Polyline key={z.id} path={z.path} onClick={() => setSelectedZoneId(z.id)} options={{ strokeColor: z.color, strokeOpacity: 0.95, strokeWeight: 4, clickable: true }} />
      ) : (
        <Circle key={z.id} center={z.center} radius={z.radius} onClick={() => setSelectedZoneId(z.id)} options={{ fillColor: z.color, fillOpacity: 0.15, strokeColor: z.color, strokeWeight: 2, clickable: true }} />
      ))}

      {zones.map((zone) => {
        if (selectedZoneId !== zone.id) return null;
        const position = shapeLabelPos(zone);
        if (!position) return null;
        const importance = IMPORTANCE[zone.importance] || IMPORTANCE.response;
        return (
          <InfoWindow key={`zone-info-${zone.id}`} position={position} onCloseClick={() => setSelectedZoneId(null)}>
            <div className="min-w-[190px] max-w-[250px] p-1" data-testid={`gis-zone-info-${zone.id}`}>
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: zone.color || importance.color }} />
                <p className="text-sm font-bold text-gray-900">
                  {zone.label || (zone.kind === 'polyline' ? 'Operational line' : zone.kind === 'circle' ? 'Radius zone' : 'Operational zone')}
                </p>
              </div>
              <p className="mt-1 text-[11px] font-semibold text-gray-500">{importance.label} · {shapeMeasure(zone)}</p>
              {zone.note && <p className="mt-1 text-xs text-gray-700">{zone.note}</p>}
            </div>
          </InfoWindow>
        );
      })}

      {/* Real-world measurement labels: line length, circle radius, zone area */}
      {zones.map(z => {
        const pos = shapeLabelPos(z);
        if (!pos) return null;
        return (
          <Marker
            key={`measure-${z.id}`}
            position={pos}
            clickable
            onClick={() => setSelectedZoneId(z.id)}
            zIndex={90}
            icon={{ path: 'M 0,0 L 0.1,0 Z', scale: 1, strokeOpacity: 0 }}
            label={{ text: shapeMeasure(z), color: '#111827', fontSize: '11px', fontWeight: '700', className: 'gis-measure-label' }}
          />
        );
      })}

      {sequenceOn && sequencePath.length > 1 && (
        <>
          <Polyline path={sequencePath} options={{ strokeColor: '#0284c7', strokeOpacity: 0.9, strokeWeight: 3, geodesic: true, icons: [{ icon: { path: g.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 3 }, offset: '50%', repeat: '120px' }] }} />
          <Marker
            position={sequencePath[Math.floor(sequencePath.length / 2)]}
            clickable={false}
            zIndex={90}
            icon={{ path: 'M 0,0 L 0.1,0 Z', scale: 1, strokeOpacity: 0 }}
            label={{ text: `route ${_fmtKm(pathLengthKm(sequencePath))}`, color: '#0369a1', fontSize: '11px', fontWeight: '700', className: 'gis-measure-label' }}
          />
        </>
      )}

      {tasks.map(t => t.position && (
        <Marker key={t.id} position={t.position} icon={taskIcon(t.importance)} zIndex={120}
          onClick={() => setSelectedTaskId(t.id)}>
          {selectedTaskId === t.id && (
            <InfoWindow onCloseClick={() => setSelectedTaskId(null)}>
              <div className="min-w-[230px] max-w-[260px] p-1" data-testid={`gis-task-info-${t.id}`}>
                <div className="flex items-start gap-2 mb-1.5">
                  <span className="w-3 h-3 rounded-full mt-1 shrink-0" style={{ backgroundColor: importanceColor(t.importance) }} />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900 leading-tight">{t.title || 'Untitled task'}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className="text-[10px] font-semibold text-gray-500">{t.category}</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: importanceColor(t.importance) }}>{IMPORTANCE[t.importance]?.label}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${t.status === 'done' ? 'bg-emerald-100 text-emerald-700' : t.status === 'in_progress' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{(t.status || 'open').replace('_', ' ')}</span>
                    </div>
                  </div>
                </div>
                {t.note && <p className="text-xs text-gray-600 mb-2 ml-5">{t.note}</p>}

                {(t.tasks || []).length > 0 && (
                  <div className="mb-2 rounded-lg bg-gray-50 border border-gray-100 p-2">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Action tasks</p>
                      <span className="text-[10px] font-bold text-gray-500">{t.tasks.filter(s => s.done).length}/{t.tasks.length} done</span>
                    </div>
                    {t.tasks.map((s, i) => (
                      <button key={i} onClick={() => toggleSubtask(t.id, i)}
                        className="flex items-start gap-1.5 w-full text-left py-0.5 hover:bg-white rounded" data-testid={`gis-subtask-${t.id}-${i}`}>
                        {s.done
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                          : <CircleIcon className="w-3.5 h-3.5 text-gray-300 mt-0.5 shrink-0" />}
                        <span className={`text-[11px] ${s.done ? 'line-through text-gray-400' : 'text-gray-700'}`}>{s.text}</span>
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex gap-3 ml-0.5">
                  {!readOnly && <button onClick={() => onEditTask(t)} className="text-[11px] font-bold text-sky-700 hover:underline" data-testid={`gis-edit-${t.id}`}>Edit</button>}
                  {!readOnly && <button onClick={() => { setTasks(ts => ts.filter(x => x.id !== t.id)); setSelectedTaskId(null); }} className="text-[11px] font-bold text-rose-600 hover:underline" data-testid={`gis-delete-${t.id}`}>Delete</button>}
                </div>
              </div>
            </InfoWindow>
          )}
        </Marker>
      ))}

      {/* Map points of interest (assembly points, info points, etc.) */}
      {points.map(pt => pt.position && (
        <Marker key={pt.id} position={pt.position} icon={pointIcon(pt.type)} zIndex={130}
          label={g ? { text: POINT_TYPES[pt.type]?.glyph || 'i', color: '#ffffff', fontSize: '10px', fontWeight: 'bold' } : undefined}
          onClick={() => setSelectedPointId(pt.id)}>
          {selectedPointId === pt.id && (
            <InfoWindow onCloseClick={() => setSelectedPointId(null)}>
              <div className="min-w-[210px] max-w-[260px] p-1" data-testid={`gis-point-info-${pt.id}`}>
                <div className="flex items-start gap-2 mb-1.5">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold mt-0.5 shrink-0" style={{ backgroundColor: POINT_TYPES[pt.type]?.color || '#2563eb' }}>{POINT_TYPES[pt.type]?.glyph || 'i'}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900 leading-tight">{pt.label || POINT_TYPES[pt.type]?.label || 'Point'}</p>
                    <span className="text-[10px] font-semibold text-gray-500">{POINT_TYPES[pt.type]?.label || 'Info Point'}</span>
                  </div>
                </div>
                {pt.info && <p className="text-xs text-gray-600 mb-2 whitespace-pre-wrap">{pt.info}</p>}
                {(pt.details || []).filter(d => d.label || d.value).length > 0 && (
                  <div className="mb-2 rounded-lg bg-gray-50 border border-gray-100 p-2 space-y-0.5">
                    {pt.details.filter(d => d.label || d.value).map((d, i) => (
                      <div key={i} className="flex justify-between gap-2 text-[11px]">
                        <span className="font-semibold text-gray-500">{d.label}</span>
                        <span className="text-gray-800 text-right">{d.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-3 ml-0.5">
                  {!readOnly && onEditPoint && <button onClick={() => { onEditPoint(pt); setSelectedPointId(null); }} className="text-[11px] font-bold text-sky-700 hover:underline" data-testid={`gis-point-edit-${pt.id}`}>Edit</button>}
                  {!readOnly && setPoints && <button onClick={() => { setPoints(ps => ps.filter(x => x.id !== pt.id)); setSelectedPointId(null); }} className="text-[11px] font-bold text-rose-600 hover:underline" data-testid={`gis-point-delete-${pt.id}`}>Delete</button>}
                </div>
              </div>
            </InfoWindow>
          )}
        </Marker>
      ))}

      {drawShape !== 'none' && (
        <DrawingManager
          options={{ drawingControl: false }}
          drawingMode={drawShape}
          onOverlayComplete={handleOverlayComplete}
        />
      )}
    </>
  );
};

// ── Task create/edit modal ─────────────────────────────────────────────────────
export const TaskFormModal = ({ draft, onSave, onClose, actionCards = [] }) => {
  const [form, setForm] = useState(draft || {});
  useEffect(() => { if (draft) setForm(draft); }, [draft]);
  if (!draft) return null;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onPickCard = (cardId) => {
    if (!cardId) { setForm(f => ({ ...f, cardId: '', tasks: [] })); return; }
    const card = actionCards.find(c => c.id === cardId);
    if (!card) return;
    setForm(f => ({
      ...f,
      cardId: card.id,
      title: f.title?.trim() ? f.title : card.card_name,
      tasks: (card.action_tasks || []).map(t => ({ text: t.task_text, done: false })),
    }));
  };
  const toggleTask = (i) => setForm(f => ({ ...f, tasks: (f.tasks || []).map((t, idx) => idx === i ? { ...t, done: !t.done } : t) }));

  return createPortal(
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4" data-testid="gis-task-modal" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[400px] max-w-full max-h-[88vh] overflow-y-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">{draft.id ? 'Edit map task' : 'New map task'}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        {/* Action Card selector — same library as the incident timeline */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Action card (optional)</label>
          <select value={form.cardId || ''} onChange={(e) => onPickCard(e.target.value)} data-testid="gis-task-card"
            className="w-full text-sm border border-gray-300 rounded-md text-gray-900 bg-white px-2 py-2 mt-1">
            <option value="">— Custom task (no card) —</option>
            {actionCards.map(c => <option key={c.id} value={c.id}>{c.card_name}</option>)}
          </select>
        </div>

        <input autoFocus value={form.title || ''} onChange={(e) => set('title', e.target.value)} placeholder="Task title" data-testid="gis-task-title"
          className="w-full text-sm border border-gray-300 rounded-md text-gray-900 bg-white px-2 py-2" />
        <textarea value={form.note || ''} onChange={(e) => set('note', e.target.value)} placeholder="Note / details" rows={2}
          className="w-full text-xs border border-gray-300 rounded-md text-gray-900 bg-white px-2 py-2" />

        {/* Action card task checklist */}
        {(form.tasks || []).length > 0 && (
          <div className="border border-gray-100 rounded-lg p-2 bg-gray-50 space-y-1.5" data-testid="gis-task-checklist">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Tasks ({form.tasks.filter(t => t.done).length}/{form.tasks.length})</p>
            {form.tasks.map((t, i) => (
              <label key={i} className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={!!t.done} onChange={() => toggleTask(i)} className="mt-0.5" />
                <span className={t.done ? 'line-through text-gray-400' : ''}>{t.text}</span>
              </label>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <select value={form.category || 'Rescue'} onChange={(e) => set('category', e.target.value)} className="text-xs border border-gray-300 rounded-md text-gray-900 bg-white px-2 py-2">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={form.importance || 'response'} onChange={(e) => set('importance', e.target.value)} data-testid="gis-task-importance" className="text-xs border border-gray-300 rounded-md text-gray-900 bg-white px-2 py-2">
            {Object.entries(IMPORTANCE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <select value={form.status || 'open'} onChange={(e) => set('status', e.target.value)} className="w-full text-xs border border-gray-300 rounded-md text-gray-900 bg-white px-2 py-2">
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
        </select>
        <button onClick={() => onSave(form)} data-testid="gis-task-save"
          className="w-full rounded-md bg-sky-700 py-2 text-sm font-bold text-white hover:bg-sky-800">
          {draft.id ? 'Save changes' : 'Add task'}
        </button>
      </div>
    </div>,
    document.body
  );
};

// ── Map point create/edit modal (assembly point, info point, etc.) ─────────────
export const PointFormModal = ({ draft, onSave, onClose }) => {
  const [form, setForm] = useState(draft || {});
  useEffect(() => { if (draft) setForm(draft); }, [draft]);
  if (!draft) return null;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const details = form.details || [];
  const setDetail = (i, k, v) => setForm(f => ({ ...f, details: (f.details || []).map((d, idx) => idx === i ? { ...d, [k]: v } : d) }));
  const addDetail = () => setForm(f => ({ ...f, details: [...(f.details || []), { label: '', value: '' }] }));
  const removeDetail = (i) => setForm(f => ({ ...f, details: (f.details || []).filter((_, idx) => idx !== i) }));

  return createPortal(
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4" data-testid="gis-point-modal" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[400px] max-w-full max-h-[88vh] overflow-y-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">{draft.id ? 'Edit map point' : 'New map point'}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Point type</label>
          <select value={form.type || 'info'} onChange={(e) => set('type', e.target.value)} data-testid="gis-point-type-select"
            className="w-full text-sm border border-gray-300 rounded-md text-gray-900 bg-white px-2 py-2 mt-1">
            {Object.entries(POINT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        <input autoFocus value={form.label || ''} onChange={(e) => set('label', e.target.value)} placeholder="Label (e.g. Main Assembly Area, House #12)" data-testid="gis-point-label"
          className="w-full text-sm border border-gray-300 rounded-md text-gray-900 bg-white px-2 py-2" />
        <textarea value={form.info || ''} onChange={(e) => set('info', e.target.value)} placeholder="Information / general info (e.g. This location is a 3-floor residential building)" rows={3} data-testid="gis-point-info"
          className="w-full text-xs border border-gray-300 rounded-md text-gray-900 bg-white px-2 py-2" />

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Custom details</label>
            <button onClick={addDetail} className="text-[11px] font-bold text-sky-700 hover:underline" data-testid="gis-point-add-detail">+ Add field</button>
          </div>
          {details.map((d, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input value={d.label} onChange={(e) => setDetail(i, 'label', e.target.value)} placeholder="Label" className="flex-1 text-xs border border-gray-300 rounded-md px-2 py-1.5" />
              <input value={d.value} onChange={(e) => setDetail(i, 'value', e.target.value)} placeholder="Value" className="flex-1 text-xs border border-gray-300 rounded-md px-2 py-1.5" />
              <button onClick={() => removeDetail(i)}><X className="w-3.5 h-3.5 text-gray-400" /></button>
            </div>
          ))}
        </div>

        <button onClick={() => onSave(form)} data-testid="gis-point-save"
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold py-2 rounded-md">
          {draft.id ? 'Save changes' : 'Add point'}
        </button>
      </div>
    </div>,
    document.body
  );
};
